import { ORIGINAL_SIZE, PhotoGallery, SCALED_PHOTO_SIZES } from '../contexts';
import { PreWarmCacheManager } from '../contexts/PreWarmCache';
import { CachePhotosOptions, PhotoSize } from '../contexts/syncing';
import { SYNC_RECORD_MAX_AGE_MS } from '../contexts/syncing/persistence';
import { HardFailureError } from '../contexts/syncing/ScanSynchronizer/syncLib';
import { ConnectionInfo, FileCacheInterface, FileSystemInterface } from '../FileSystem';
import { fileUriToPath, Mutex, safeFsForEach, safeFsOp } from '../lib';
import { type RealtimeClient } from '../realtime';
import { GalleryPhotoLoader, getCacheFileName } from './GalleryPhotoLoader';
import { createLegacyPhotoMigration } from './legacyPhotoMigration';
import { buildScanPhotoPreWarmItems } from './scanPhotoPreWarm';
import { UnsyncedPhotoIndex } from './UnsyncedPhotoIndex';

const MAX_PHOTO_DIMENSION = 2048;
const MAX_PHOTO_BYTES = 1024 * 1024 * 1.1; // 1MB + 10% buffer

/**
 * The expectation is ensureFreeSpace will be called for every photo capture.
 * Over-free so we don't repeat the work for every iteration.
 */
const ENSURE_FREE_SPACE_OVERFREE_RATIO = 2;

/** All possible scaled sizes. Used to enumerate files for deletePhoto. */

const ensureDirectory = async (FileSystem: FileSystemInterface, path: string) => {
  if (!(await FileSystem.getDirectoryExists(path))) {
    await FileSystem.makeDirectoryAsync(path, { intermediates: true });
  }
};

/**
 * Ensure directory is a bare absolute path with trailing slash.
 * Strips file: URI prefix if present (Expo's Paths API returns URIs).
 */
const normalizeDirectory = (dir: string): string => {
  const path = dir.startsWith('file:') ? fileUriToPath(dir) : dir;
  return path.endsWith('/') ? path : path + '/';
};

export const createFileSystemPhotoGallery = ({
  FileSystem,
  fileCache,
  getConnectionInfo,
  realtimeClient,
  missingPhotoMaxWaitMs,
}: {
  FileSystem: FileSystemInterface;
  fileCache: FileCacheInterface;
  getConnectionInfo: () => ConnectionInfo;
  realtimeClient?: RealtimeClient;
  missingPhotoMaxWaitMs?: number;
}): PhotoGallery => {
  if (!FileSystem.documentDirectory) throw new Error('Document and cache directories must be provided');
  if (!FileSystem.cacheDirectory) throw new Error('Cache directory must be provided');
  const documentDirectory = normalizeDirectory(FileSystem.documentDirectory);
  const cacheDirectory = normalizeDirectory(FileSystem.cacheDirectory);
  const stagingDir = `${documentDirectory}cache-staging/`;

  // ********************************************
  // Staging dir
  // ********************************************
  const stagingDirCreated = ensureDirectory(FileSystem, stagingDir);
  // Sweep orphans from a previous crashed download. Best-effort; never blocks normal ops.
  stagingDirCreated.then(async () => {
    const { files } = await FileSystem.getDirectoryContents(stagingDir);
    await safeFsForEach(
      files,
      file => `sweep staging: failed to delete ${file}`,
      file => FileSystem.deleteAsync(`${stagingDir}${file}`),
    );
  }).catch(() => {/* tolerate */});

  // ********************************************
  // Doc-dir unsynced index (the non-cache half of the gallery)
  // ********************************************
  const unsyncedPhotoIndex = new UnsyncedPhotoIndex({ fileSystem: FileSystem, documentDirectory });

  // ********************************************
  // Photo loader — owns "do we have it / should we fetch / dedup concurrent asks"
  // ********************************************
  const photoLoader = new GalleryPhotoLoader({
    fileSystem: FileSystem,
    fileCache,
    stagingDir,
    ensureStagingDirExists: () => stagingDirCreated,
    getUnsyncedPhotoPath: photoId => unsyncedPhotoIndex.getPathForPhoto(photoId),
    getConnectionInfo,
    realtimeClient,
    missingPhotoMaxWaitMs,
  });

  // ********************************************
  // getPhotoPath — used by getPhotoData / uploadPhoto / deletePhoto (NOT the URI hot path)
  // ********************************************
  /**
   * Find a photo locally. Returns the file path if found, or null.
   * Checks unsynced index first, then the FileCache.
   */
  const getExistingPhotoPath = async (photoId: string, size?: PhotoSize): Promise<string | null> => {
    if (!size || size === ORIGINAL_SIZE) {
      // Scaled photos are never unsynced; only check unsynced for originals.
      const path = await unsyncedPhotoIndex.getPathForPhoto(photoId);
      if (path && await FileSystem.getFileExists(path)) return path;
    }
    // getFile both returns the path and freshens LRU — appropriate since the caller
    // is about to use this file (upload, download, read).
    return (await fileCache.getFile(getCacheFileName(photoId, size ?? ORIGINAL_SIZE))) ?? null;
  };

  // ********************************************
  // freeCacheSpace
  // ********************************************
  const freeCacheSpace = async (bytesToFree: number) => {
    if (bytesToFree <= 0) return { bytesFreed: 0, numDeleted: 0 };
    const startTime = Date.now();
    try {
      const { evicted, evictedBytes } = await fileCache.evict(bytesToFree);
      const { freeStorageSpace } = await FileSystem.getPhotoGalleryStorageInfo();
      const result = { bytesFreed: evictedBytes, numDeleted: evicted.length, freeStorageSpace };
      if (evicted.length > 0) console.info('freeCacheSpace successful', result);
      const duration = Date.now() - startTime;
      console[duration > 5000 ? 'error' : 'info'](`freeCacheSpace: success`, { duration, result });
      return result;
    } catch (error) {
      console.error(`freeCacheSpace: failed`, error);
      throw error;
    }
  };

  // ********************************************
  // Legacy → FileCache migration (one-shot, deletable once all installs migrate)
  // ********************************************
  const { needsMigration, getMigrationInfo, migrate } = createLegacyPhotoMigration({
    fileSystem: FileSystem,
    fileCache,
    cacheDirectory,
  });

  // ****************************************************************************************
  // PhotoGallery interface
  // ****************************************************************************************
  const mutex = new Mutex();
  // Dedup concurrent setPhotoFromUri calls for the same photoId — defensive, not the expected use case.
  const _inFlightSetPhotoFromUri = new Map<string, Promise<void>>();

  return {
    getPhotoUri: (photoId, size) => photoLoader.getPhoto(photoId, size ?? ORIGINAL_SIZE),
    getBestLocallyAvailablePhoto: (photoId, size, scanDirection) => photoLoader.getBestLocallyAvailablePhoto(photoId, size, scanDirection),
    getBestLocallyAvailablePhotoSync: (photoId, size) => photoLoader.getBestLocallyAvailablePhotoSync(photoId, size),

    /**
     * Read the photo's bytes into JS. Web-only — native code paths consume URIs directly
     * (e.g. `<Image source={{ uri }} />`), so the data never has to round-trip through JS there.
     */
    getPhotoData: async photoId => {
      if (!FileSystem.readAsBlobAsync) throw new Error('getPhotoData: FileSystem does not support readAsBlobAsync (web-only API)');
      let path = await getExistingPhotoPath(photoId);
      if (!path) {
        // Trigger download into the FileCache, then look up the resulting path.
        const uri = await photoLoader.getPhoto(photoId, ORIGINAL_SIZE);
        if (!uri) return undefined;
        path = await getExistingPhotoPath(photoId);
      }
      return path ? FileSystem.readAsBlobAsync(path) : undefined;
    },

    uploadPhoto: async (scanId, photoId, timeoutSeconds?, onProgress?) => {
      const path = await getExistingPhotoPath(photoId);
      if (!path) throw new HardFailureError(`uploadPhoto: photo ${photoId} not found locally (scanId: ${scanId})`);
      await FileSystem.uploadPhoto(path, scanId, photoId, getConnectionInfo(), timeoutSeconds, onProgress);
    },

    setPhoto: async (scanId, photoId, buffer) =>
      mutex.runExclusive(async () => {
        if (!FileSystem.writeAsBlobAsync) throw new Error('Provided FileSystem does not support writeAsBlobAsync; use setPhotoFromUri instead');
        await unsyncedPhotoIndex.ensureScanDir(scanId);
        const filePath = unsyncedPhotoIndex.getPhotoPath(scanId, photoId);
        await FileSystem.writeAsBlobAsync(filePath, buffer);
        await unsyncedPhotoIndex.add(photoId, scanId);
      }),

    setPhotoFromUri: async (scanId, photoId, uri) => {
      const inFlight = _inFlightSetPhotoFromUri.get(photoId);
      if (inFlight) return inFlight;

      const promise = (async () => {
        let sourcePath: string;
        try {
          sourcePath = fileUriToPath(uri);
        } catch (error) {
          console.error(`setPhotoFromUri: failed to parse URI (scanId: ${scanId}, photoId: ${photoId}, uri: ${uri})`, error);
          throw error;
        }
        await unsyncedPhotoIndex.ensureScanDir(scanId);
        const filePath = unsyncedPhotoIndex.getPhotoPath(scanId, photoId);
        try {
          await FileSystem.moveAsync({ from: sourcePath, to: filePath });
          const fileInfo = await FileSystem.getFileInfo(filePath);
          if (fileInfo.size && fileInfo.size > MAX_PHOTO_BYTES) {
            await stagingDirCreated;
            const tempPath = `${stagingDir}${photoId}_normalizing.jpg`;
            await FileSystem.scaleImageFile(filePath, tempPath, MAX_PHOTO_DIMENSION);
            await mutex.runExclusive(async () => {
              if (!(await FileSystem.getFileExists(tempPath))) {
                throw new Error(`setPhotoFromUri: normalizing file missing (photoId: ${photoId}, tempPath: ${tempPath})`);
              }
              await FileSystem.deleteAsync(filePath);
              await FileSystem.moveAsync({ from: tempPath, to: filePath });
            });
          }
        } catch (error) {
          console.error(`setPhotoFromUri: move/normalize failed (scanId: ${scanId}, photoId: ${photoId}, from: ${sourcePath}, to: ${filePath})`, error);
          throw error;
        }
        await unsyncedPhotoIndex.add(photoId, scanId);
      })();

      _inFlightSetPhotoFromUri.set(photoId, promise);
      promise.finally(() => _inFlightSetPhotoFromUri.delete(photoId)).catch(() => {});
      return promise;
    },

    markPhotoSynced: async (scanId, photoId) =>
      mutex.runExclusive(async () => {
        const docPath = unsyncedPhotoIndex.getPhotoPath(scanId, photoId);
        if (!(await FileSystem.getFileExists(docPath))) return;
        await fileCache.addFile(docPath);
        await unsyncedPhotoIndex.remove(photoId);
      }),

    isPhotoSynced: async photoId => (await fileCache.getFileInfo(getCacheFileName(photoId, ORIGINAL_SIZE))) !== undefined,

    isPhotoUnsynced: photoId => unsyncedPhotoIndex.has(photoId),

    deletePhoto: async photoId =>
      mutex.runExclusive(async () => {
        // Delete from unsynced (doc dir)
        const path = await unsyncedPhotoIndex.getPathForPhoto(photoId);
        if (path) {
          if (await FileSystem.getFileExists(path)) await FileSystem.deleteAsync(path);
          await unsyncedPhotoIndex.remove(photoId);
        }

        // Delete from FileCache: original + every possible scaled size.
        await fileCache.evictFile(getCacheFileName(photoId, ORIGINAL_SIZE));
        for (const size of SCALED_PHOTO_SIZES) {
          await fileCache.evictFile(getCacheFileName(photoId, size));
        }
      }),

    clearPhotoCache: async () => {
      const { evicted, evictedBytes } = await fileCache.evict(Infinity);
      return { numDeleted: evicted.length, bytesFreed: evictedBytes };
    },

    dangerouslyEraseUnsyncedPhotos: async () => {
      let numDeleted = 0;
      let bytesFreed = 0;
      for (const scanId of await unsyncedPhotoIndex.getScanIds()) {
        await mutex.runExclusive(async () => {
          const photoIds = await unsyncedPhotoIndex.getPhotoIdsForScan(scanId);
          for (const photoId of photoIds) {
            bytesFreed += (await FileSystem.getFileInfo(unsyncedPhotoIndex.getPhotoPath(scanId, photoId))).size ?? 0;
            numDeleted++;
          }
          await unsyncedPhotoIndex.deleteScanDir(scanId);
        });
      }
      await unsyncedPhotoIndex.rebuild();
      return { numDeleted, bytesFreed };
    },

    deleteExpiredUnsyncedPhotos: async () =>
      mutex.runExclusive(async () => {
        const now = Date.now();
        let numDeleted = 0;
        let bytesFreed = 0;

        for (const [photoId, scanId] of await unsyncedPhotoIndex.getScanIdsByPhotoId()) {
          const path = unsyncedPhotoIndex.getPhotoPath(scanId, photoId);
          try {
            const info = await FileSystem.getFileInfo(path);
            if (info.modificationTime && (now - info.modificationTime > SYNC_RECORD_MAX_AGE_MS)) {
              const ageDays = Math.round((now - info.modificationTime) / (24 * 60 * 60 * 1000));
              console.error(`deleteExpiredUnsyncedPhotos: deleting unsynced photoId ${photoId}`, { photoId, scanId, ageDays });
              await FileSystem.deleteAsync(path);
              await unsyncedPhotoIndex.remove(photoId);
              bytesFreed += info.size ?? 0;
              numDeleted++;
            }
          } catch (error) {
            console.warn(`deleteExpiredUnsyncedPhotos: failed to process ${photoId}`, error, { photoId, scanId });
          }
        }

        return { numDeleted, bytesFreed };
      }),

    getPhotoGalleryInfo: async () => {
      const unsyncedPhotos = await unsyncedPhotoIndex.getStats();
      const cacheInfo = await fileCache.getCacheInfo();
      const cachedPhotos = { numBytes: cacheInfo.cacheSize, numPhotos: cacheInfo.fileCount };
      return {
        numPhotos: unsyncedPhotos.numPhotos + cachedPhotos.numPhotos,
        numBytes: unsyncedPhotos.numBytes + cachedPhotos.numBytes,
        unsyncedPhotos,
        cachedPhotos,
      };
    },

    getPhotoGalleryStorageInfo: async () => FileSystem.getPhotoGalleryStorageInfo?.() ?? null,

    cleanupEmptyScanDirectories: async () => {
      for (const scanId of await unsyncedPhotoIndex.getScanDirsOnDisk()) {
        // Tolerate per-scan failures: directory may have been deleted by another
        // path in between mutex acquisitions; rather skip than block.
        await mutex.runExclusive(() =>
          safeFsOp(
            `cleanupEmptyScanDirectories: failed to delete ${scanId}`,
            async () => {
              const files = await unsyncedPhotoIndex.getFilesInScanDir(scanId);
              if (files.length === 0) await unsyncedPhotoIndex.deleteScanDir(scanId);
            },
          )
        );
      }
    },

    freeCacheSpace,

    ensureFreeSpace: async bytesNeeded => {
      const { freeStorageSpace } = await FileSystem.getPhotoGalleryStorageInfo();
      const bytesToFree = bytesNeeded - freeStorageSpace;
      if (bytesToFree <= 0) return { bytesFreed: 0, numDeleted: 0, freeStorageSpace };
      return freeCacheSpace(bytesToFree * ENSURE_FREE_SPACE_OVERFREE_RATIO);
    },

    cachePhotos: (photoIds, size, onProgress?: (progress: number) => void, options?: CachePhotosOptions) => {
      // Build each photo's cache key + fetch URL and hand the batch to the
      // pre-warm system, which freshens already-cached photos, downloads only the
      // misses (concurrency-managed), and reports progress. The scaling/retry
      // path lives in getPhoto for on-demand reads; bulk warming stays simple.
      const resolvedSize = size ?? ORIGINAL_SIZE;
      const list = buildScanPhotoPreWarmItems(getConnectionInfo().apiUrl, photoIds, resolvedSize);

      // 1=147s, 5=34s, 10=25s, 20=24s, 50=24s — measured on iPad Pro 13" Gen 1 against local dev.
      const preWarmCache = new PreWarmCacheManager(fileCache, { concurrency: options?.concurrency ?? 10 });
      return new Promise<number>(resolve => {
        const unsubscribe = preWarmCache.subscribe('cachePhotos', list, status => {
          onProgress?.(status.progress);
          if (status.done) {
            unsubscribe();
            resolve(status.completed);
          }
        });
      });
    },

    getUnsyncedPhotoIdsForScan: scanId => unsyncedPhotoIndex.getPhotoIdsForScan(scanId),
    getUnsyncedScanIds: () => unsyncedPhotoIndex.getScanIds(),
    getUnsyncedPhotoIdsByScanId: () => unsyncedPhotoIndex.getPhotoIdsByScanId(),

    needsMigration,
    getMigrationInfo,
    migrate,
  };
};
